import React, {useEffect, useState, useRef, useContext} from 'react'
import {Link} from 'react-router-dom'
import {Button, Container, Grid, TextField, InputAdornment, Box} from '@material-ui/core'
import ListIcon from '@material-ui/icons/List'
import ViewIcon from '@material-ui/icons/ViewModule'
import ReorderIcon from '@material-ui/icons/Reorder'
import ViewAgendaIcon from '@material-ui/icons/ViewAgenda'
import {ToggleButtonGroup, ToggleButton} from '@material-ui/lab'
import ListView from './BarcodeLibraryListView'
import GridView from './BarcodeLibraryGridView'
import {Query} from 'react-apollo'
import {libraryPageQuery} from '../../graphql/barcode/queries/libraryPageQuery'
import {automaticGenerated, fieldValue, tableSheets, tableLabels} from './tableTitles'
import TableActions from '../BarcodeLibraryTableActions'
import LoadingScreen from '../BarcodeLoadingScreen'
import {useTranslation} from 'react-i18next'
import SearchIcon from '@material-ui/icons/Search'
import useDebounce from './useDebounce'
import {BarcodeUrlSkipContext} from '../../context/BarcodeUrlSkip'
import useStyles from './BarcodeLibraryPage.styles'
import {BarcodeUserSettingsContext} from '../../context/BarcodeUserSettings'
import {BarcodeLabelRecordContext} from '../../context/BarcodeLabelRecord'
import {BarcodeSheetRecordContext} from '../../context/BarcodeSheetRecord'

export default function BarcodeLibraryPage(props) {
  const classes = useStyles()
  const {t} = useTranslation()
  const [view, setView] = React.useState('list')
  const [density, setDensity] = React.useState('expand')
  const {userSettings} = useContext(BarcodeUserSettingsContext)
  const accessToken = JSON.parse(window.localStorage.getItem('token'))
  const userId = parseInt(window.localStorage.getItem('user_id'))
  const [searchCondition, setSearchCondition] = useState('')
  const [showSearchField, setShowSearchField] = useState(false)
  const debouncedSearchTerm = useDebounce(searchCondition, 1000)
  const inputRef = useRef()
  const {setUrlGoTo} = useContext(BarcodeUrlSkipContext)
  const {setBarcodeLabelRecord} = useContext(BarcodeLabelRecordContext)
  const {setBarcodeSheetRecord} = useContext(BarcodeSheetRecordContext)

  useEffect(() => {
    if (window !== window.parent) {
      window.parent.postMessage('scrollTop', process.env.REACT_APP_BARCODE_WINDOW_URL)
    } else {
      window.scrollTo(0, 0)
    }
  }, [])

  useEffect(() => {
    if (showSearchField) {
      inputRef.current.focus()
    }
  }, [showSearchField, inputRef])

  const handleView = (event, newView) => {
    if (newView) {
      setView(newView)
    }
  }

  const handleDensity = (event, newDensity) => {
    if (newDensity) {
      setDensity(newDensity)
    }
  }

  function tableRowData(data, showTable, labels) {
    let tableData = []

    data.map(row => {
      let dataObject = {
        name: row.name,
      }
      if (showTable === 'automated') {
        dataObject['label'] = row.label === null ? '' : row.label.name
        dataObject['digits'] = row.digits
        dataObject['lastGenerated'] = row.lastPrinted
      } else if (showTable === 'fieldValue') {
        dataObject['label'] = row.label === null ? '' : row.label.name
        dataObject['entityId'] = row.entityName
        dataObject['fieldId'] = row.fieldName
      } else if (showTable === 'sheets') {
        dataObject['size'] = row.size
        dataObject['rows'] = row.rows
        dataObject['columns'] = row.columns
      } else {
        dataObject['width'] = row.width
        dataObject['height'] = row.height
      }
      dataObject['groupAction'] = (
        <TableActions
          showTable={showTable}
          record={row}
          accountId={userSettings.accountId}
          labelsRecord={labels}
        />
      )
      tableData.push(dataObject)
      return tableData
    })

    return tableData
  }

  function wizardPage(event) {
    setUrlGoTo('')
    setBarcodeLabelRecord('')
    setBarcodeSheetRecord('')
  }

  // eslint-disable-next-line
  const handleClickAway = () => {
    if (searchCondition) {
      return
    }
    setShowSearchField(false)
  }

  return (
    <div>
      <Grid container>
        <Grid container item xs={6} justify="flex-start">
          <Container>
            <Button
              variant="outlined"
              className={classes.buttonLink}
              color="primary"
              component={Link}
              to="barcode-wizard"
              onClick={wizardPage}
            >
              {t('barcode.library.library-page.go-to-wizard')}
            </Button>

            <TextField
              id="search-table"
              value={searchCondition}
              variant="outlined"
              onChange={e => setSearchCondition(e.target.value)}
              type="search"
              className={classes.searchStyles}
              inputRef={inputRef}
              InputProps={{
                endAdornment: (
                  <InputAdornment>
                    <SearchIcon className={classes.searchColor} />
                  </InputAdornment>
                ),
                className: classes.inputStyles,
              }}
            />
          </Container>
        </Grid>

        <Grid container item xs={6} justify="flex-end">
          <Box marginTop={6} hidden={view === 'grid'}>
            {t('barcode.library.library-page.display-density')}
          </Box>
          <div hidden={view === 'grid'}>
            <ToggleButtonGroup
              value={density}
              exclusive
              onChange={handleDensity}
              className={classes.toggleButtonMargin}
              size="small"
            >
              <ToggleButton value="expand">
                <ViewAgendaIcon color={density === 'expand' ? 'primary' : 'inherit'} />
              </ToggleButton>
              <ToggleButton value="compress">
                <ReorderIcon color={density === 'compress' ? 'primary' : 'inherit'} />
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
          <Box marginTop={6}>{t('barcode.library.library-page.view')}</Box>
          <div>
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={handleView}
              className={classes.toggleButtonMargin}
              size="small"
            >
              <ToggleButton value="list">
                {<ListIcon color={view === 'list' ? 'primary' : 'inherit'} />}
              </ToggleButton>
              <ToggleButton value="grid">
                {<ViewIcon color={view === 'grid' ? 'primary' : 'inherit'} />}
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        </Grid>
      </Grid>

      <Box mx={4}>
        <Query
          query={libraryPageQuery}
          variables={{
            accountId: parseInt(userSettings.accountId),
            token: accessToken,
            userId: userId,
            filter: debouncedSearchTerm,
          }}
        >
          {({loading, error, data}) => {
            if (loading) {
              return (
                <div>
                  <LoadingScreen size={50} />
                </div>
              )
            }
            if (error) {
              return (
                <div>
                  {props.history.push({
                    pathname: 'pageNotFound',
                  })}
                </div>
              )
            }

            let dataRecords = data

            if (dataRecords && view === 'list') {
              return (
                <div>
                  <ListView
                    density={density}
                    showTable="automated"
                    title={t('barcode.library.titles.auto')}
                    barcodeSettings={dataRecords.libraryPage.automated}
                    tableShow={automaticGenerated}
                    rowData={tableRowData(
                      dataRecords.libraryPage.automated,
                      'automated',
                      dataRecords.libraryPage.labels
                    )}
                  />

                  <ListView
                    density={density}
                    showTable="fieldValue"
                    title={t('barcode.library.titles.field-value')}
                    barcodeSettings={data.libraryPage.fieldValue}
                    tableShow={fieldValue}
                    rowData={tableRowData(
                      data.libraryPage.fieldValue,
                      'fieldValue',
                      data.libraryPage.labels
                    )}
                  />

                  <ListView
                    density={density}
                    showTable="sheets"
                    title={t('barcode.library.titles.sheet-templates')}
                    barcodeSettings={data.libraryPage.sheets}
                    tableShow={tableSheets}
                    rowData={tableRowData(data.libraryPage.sheets, 'sheets')}
                  />

                  <ListView
                    density={density}
                    showTable="labels"
                    title={t('barcode.library.titles.label-templates')}
                    barcodeSettings={data.libraryPage.labels}
                    tableShow={tableLabels}
                    rowData={tableRowData(data.libraryPage.labels, 'labels')}
                  />
                </div>
              )
            } else if (dataRecords) {
              return (
                <div>
                  <GridView
                    record={data.libraryPage.automated}
                    labelsRecord={data.libraryPage.labels}
                    showTable="automated"
                    viewTitle="Automatically Generated Barcodes"
                    itemOne="Digits"
                    itemTwo="Last Generated"
                  />

                  <GridView
                    record={data.libraryPage.fieldValue}
                    labelsRecord={data.libraryPage.labels}
                    showTable="fieldValue"
                    viewTitle="Barcodes Generated From Field Value"
                    itemOne="Group"
                    itemTwo="Field"
                  />

                  <GridView
                    record={data.libraryPage.sheets}
                    showTable="sheets"
                    viewTitle="Sheet Templates"
                    itemOne="Rows"
                    itemTwo="Columns"
                  />

                  <GridView
                    record={data.libraryPage.labels}
                    showTable="labels"
                    viewTitle="Label Templates"
                    itemOne="Width"
                    itemTwo="Height"
                  />
                </div>
              )
            }
          }}
        </Query>
      </Box>
    </div>
  )
}
